import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ToastrService} from 'ngx-toastr';
import {ApiService} from "@ctms/api.service";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {NgSelectModule} from "@ng-select/ng-select";
import {NgxDatatableModule} from "@swimlane/ngx-datatable";
import {NgbNavModule} from "@ng-bootstrap/ng-bootstrap";
import {Paginator} from "@shared/components/paginator/paginator.component";
import {PatientRecordAppointmentNotesService} from "@shared/api/services/v2/patient-record-appointment-notes.service";
import {RecordNoteTypeService} from "@shared/api/services/v2/library/record-note-type.service";
import {
    PatientRecordAppointmentNote,
    RecordContactType,
    RecordModalityOfTherapy,
    RecordNoteType
} from "@shared/api/models/query/patient-record-appointment-note.model";
import {RecordContactTypeService} from "@shared/api/services/v2/library/record-contact-type.service";
import {RecordModalityOfTherapyService} from "@shared/api/services/v2/library/record-modality-of-therapy.service";
import {PatientRecordAppointmentNoteUpdate} from "@shared/api/models/command/patient-record-appointment-note.model";
import {PatientsService} from "@shared/api/services/v2/patients.service";
import {Patient} from "@shared/api/models/query/patient.model";


@Component({
    selector: 'cdss-patient-records-session-notes',
    imports: [CommonModule, ReactiveFormsModule, SpinnerComponent, NgSelectModule, NgxDatatableModule, NgbNavModule],
    providers: [ApiService],
    standalone: true,
    templateUrl: './patient-records-session-notes.component.html'
})
export class PatientRecordsSessionNotesComponent extends Paginator {

    editing: boolean = false
    editingId?: number = null
    @Input() patientId: number
    patient: Patient

    recordNoteTypes: RecordNoteType[]
    recordContactTypes: RecordContactType[]
    recordModalitiesOfTherapy: RecordModalityOfTherapy[];

    form: FormGroup

    constructor(
        private toastr: ToastrService,
        private recordNoteTypeService: RecordNoteTypeService,
        private recordContactTypeService: RecordContactTypeService,
        private recordModalityOfTherapyService: RecordModalityOfTherapyService,
        private patientsService: PatientsService,
        service: PatientRecordAppointmentNotesService
    ) {
        super()
        this.service = service
    }

    ngOnInit(): void {
        this.filterForm = new FormGroup({
        });
        this.form = new FormGroup({
            appointmentContent: new FormControl(),
            patientActions: new FormControl(),
            patientHomework: new FormControl(),
            recordContactTypeId: new FormControl('', Validators.required),
            recordNoteTypeId: new FormControl(),
            recordModalityOfTherapyId: new FormControl(),
            appointmentNumber: new FormControl(),
            nextAppointment: new FormControl(),
        })

        this.service.setParent(this.patientId)
        this.refreshRows()

        this.getContactTypes()
        this.getNoteTypes()
        this.getModalitiesOfTherapy()
        this.getPatient()
    }

    async getContactTypes() {
        const list = await this.recordContactTypeService.listAll()
        this.recordContactTypes = list.data
    }

    async getNoteTypes() {
        const list = await this.recordNoteTypeService.listAll()
        this.recordNoteTypes = list.data
    }

    async getModalitiesOfTherapy() {
        const list = await this.recordModalityOfTherapyService.listAll()
        this.recordModalitiesOfTherapy = list.data
    }

    async getPatient() {
        this.patient = await this.patientsService.getById(this.patientId)
    }

    edit(row: PatientRecordAppointmentNote) {
        this.editingId = row.id
        const data = {
            appointmentContent: row.appointmentContent,
            patientActions: row.patientActions,
            patientHomework: row.patientHomework,
            recordContactTypeId: row.recordContactType == null ? null : "" + row.recordContactType.id,
            recordNoteTypeId: row.recordNoteType == null ? null : "" + row.recordNoteType?.id,
            recordModalityOfTherapyId: row.recordModalityOfTherapy == null ? null : "" + row.recordModalityOfTherapy?.id,
            appointmentNumber: row.appointmentNumber,
            nextAppointment: row.nextAppointment == null ? null : (new Date(row.nextAppointment)).toISOString().slice(0, 16),
        }
        this.form.patchValue(data)
        this.editing = true
    }

    add() {
        this.editingId = null
        this.form.reset()
        this.editing = true
    }

    cancel() {
        this.editing = false
    }

    submit() {
        if (this.form.valid) {
            this.loading = true
            const value: PatientRecordAppointmentNoteUpdate = {
                appointmentContent: this.form.value.appointmentContent,
                patientActions: this.form.value.patientActions,
                patientHomework: this.form.value.patientHomework,
                recordContactTypeId: this.form.value.recordContactTypeId == null? null : Number(this.form.value.recordContactTypeId),
                recordNoteTypeId: this.form.value.recordNoteTypeId == null? null : Number(this.form.value.recordNoteTypeId),
                recordModalityOfTherapyId: this.form.value.recordModalityOfTherapyId == null? null : Number(this.form.value.recordModalityOfTherapyId),
                appointmentNumber: this.form.value.appointmentNumber == null? null : Number(this.form.value.appointmentNumber),
                nextAppointment: this.form.value.nextAppointment,
            }
            if (this.editingId == null) {
                this.service.create(value).subscribe({
                    next: () => {
                        this.refreshRows()
                        this.toastr.success('Note successfully created', 'Success');
                        this.editing = false
                        this.loading = false
                        this.getPatient()
                    }
                })
            } else {
                this.service.update(this.editingId, value).subscribe({
                    next: () => {
                        this.refreshRows()
                        this.toastr.success('Note successfully updated', 'Success');
                        this.editing = false
                        this.loading = false
                    }
                })
            }
        } else {
            this.form.markAllAsTouched()
            this.toastr.error('Please fill all the required fields');
        }
    }
}
